import { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowRight, X, MapPin } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import type { UnifiedQuoteRow } from "@/hooks/useDirectQuoteRequests";

interface Props {
  selected: UnifiedQuoteRow;
  allRows: UnifiedQuoteRow[];
  onClear: () => void;
}

/** Same-lane matcher. Case-insensitive city+state. */
function sameLane(a: UnifiedQuoteRow, b: UnifiedQuoteRow) {
  return (
    a.origin_city.toLowerCase() === b.origin_city.toLowerCase() &&
    a.origin_state.toLowerCase() === b.origin_state.toLowerCase() &&
    a.destination_city.toLowerCase() === b.destination_city.toLowerCase() &&
    a.destination_state.toLowerCase() === b.destination_state.toLowerCase()
  );
}

function fmtMoney(n: number | null | undefined) {
  return n != null ? `$${Math.round(n).toLocaleString()}` : null;
}
function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}
function fmtDuration(ms: number) {
  if (ms < 60_000) return `${Math.round(ms / 1000)}s`;
  const m = Math.round(ms / 60_000);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  if (h < 24) return mm ? `${h}h ${mm}m` : `${h}h`;
  const d = Math.floor(h / 24);
  const rh = h % 24;
  return rh ? `${d}d ${rh}h` : `${d}d`;
}

export function LaneAnalyticsPanel({ selected, allRows, onClear }: Props) {
  // Rows on the same lane (includes the selected row).
  const laneRows = useMemo(
    () => allRows.filter((r) => sameLane(r, selected)),
    [allRows, selected]
  );

  // Card 1 — from ✅ row-level data, no fetch.
  const card1 = useMemo(() => {
    const responded = laneRows.filter((r) => r.response_count > 0);
    const lows = responded.map((r) => r.low as number).filter((n) => Number.isFinite(n));
    const highs = responded.map((r) => r.high as number).filter((n) => Number.isFinite(n));
    const avgs = responded.map((r) => r.avg as number).filter((n) => Number.isFinite(n));
    const spreadPcts = responded
      .filter((r) => r.low != null && r.high != null && (r.low as number) > 0)
      .map((r) => (((r.high as number) - (r.low as number)) / (r.low as number)) * 100);

    const requestCount = laneRows.length;
    const quotesReceived = laneRows.reduce((s, r) => s + (r.response_count || 0), 0);
    const low = lows.length ? Math.min(...lows) : null;
    const high = highs.length ? Math.max(...highs) : null;
    const avg = avgs.length ? avgs.reduce((a, b) => a + b, 0) / avgs.length : null;
    const spreadPct = spreadPcts.length
      ? spreadPcts.reduce((a, b) => a + b, 0) / spreadPcts.length
      : null;
    const converted = laneRows.filter((r) => !!r.converted_to_load_id).length;
    const conversionRate = requestCount > 0 ? converted / requestCount : null;
    const lastQuotedOn = laneRows
      .map((r) => new Date(r.created_at).getTime())
      .sort((a, b) => b - a)[0];

    // Rate-per-mile: use the SELECTED row's total_miles + its avg (or lane avg)
    // Only meaningful when miles are known on the selected row.
    const miles = selected.total_miles ?? null;
    const rpmAvg = miles && miles > 0 && avg != null ? avg / miles : null;

    return { requestCount, quotesReceived, low, avg, high, spreadPct, conversionRate, lastQuotedOn, rpmAvg, miles };
  }, [laneRows, selected]);

  // Card 2 — one extra fetch on selection, for responses on all lane rows.
  const [brokerStats, setBrokerStats] = useState<
    { avgResponseMs: number | null; uniqueBrokers: number | null; loading: boolean }
  >({ avgResponseMs: null, uniqueBrokers: null, loading: true });

  useEffect(() => {
    let cancelled = false;
    setBrokerStats({ avgResponseMs: null, uniqueBrokers: null, loading: true });

    const reqIds = laneRows.filter((r) => r.source === "request").map((r) => r.id);
    const loadIds = laneRows.filter((r) => r.source === "load").map((r) => r.id);
    if (!reqIds.length && !loadIds.length) {
      setBrokerStats({ avgResponseMs: null, uniqueBrokers: null, loading: false });
      return;
    }
    const parentCreatedAt = new Map<string, number>();
    laneRows.forEach((r) => parentCreatedAt.set(r.id, new Date(r.created_at).getTime()));

    (async () => {
      let query = supabase.from("direct_quote_responses").select("broker_user_id, created_at, request_id, direct_load_id, status");
      if (reqIds.length && loadIds.length) {
        query = query.or(`request_id.in.(${reqIds.join(",")}),direct_load_id.in.(${loadIds.join(",")})`);
      } else if (reqIds.length) {
        query = query.in("request_id", reqIds);
      } else {
        query = query.in("direct_load_id", loadIds);
      }
      const { data, error } = await query;
      if (cancelled) return;
      if (error || !data) {
        setBrokerStats({ avgResponseMs: null, uniqueBrokers: null, loading: false });
        return;
      }
      const active = data.filter((r: any) => r.status === "active" || r.status === "awarded");
      const deltas: number[] = [];
      const brokers = new Set<string>();
      active.forEach((r: any) => {
        const parentId = r.request_id || r.direct_load_id;
        const parentTs = parentId ? parentCreatedAt.get(parentId) : undefined;
        const respTs = new Date(r.created_at).getTime();
        if (parentTs && respTs > parentTs) deltas.push(respTs - parentTs);
        if (r.broker_user_id) brokers.add(r.broker_user_id);
      });
      setBrokerStats({
        avgResponseMs: deltas.length ? deltas.reduce((a, b) => a + b, 0) / deltas.length : null,
        uniqueBrokers: brokers.size || null,
        loading: false,
      });
    })();
    return () => { cancelled = true; };
  }, [laneRows]);

  // Row: label + value pair. Omit entirely when value is null/undefined.
  const Row = ({ label, value }: { label: string; value: string | null }) =>
    value == null ? null : (
      <div className="flex items-center justify-between gap-3 py-1.5">
        <span className="text-[11px] text-muted-foreground">{label}</span>
        <span className="text-xs font-semibold text-foreground tabular-nums">{value}</span>
      </div>
    );

  return (
    <aside className="space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-foreground">Lane Analytics</h2>
          <div className="mt-0.5 flex items-center gap-1 text-[11px] text-muted-foreground">
            <MapPin className="h-3 w-3 text-brand-teal shrink-0" />
            <span className="truncate">
              {selected.origin_city}, {selected.origin_state}
            </span>
            <ArrowRight className="h-3 w-3 shrink-0" />
            <span className="truncate">
              {selected.destination_city}, {selected.destination_state}
            </span>
          </div>
        </div>
        <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={onClear} title="Clear lane">
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Card 1 — lane activity */}
      <Card className="p-4">
        <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2">
          Lane Activity
        </div>
        <div className="divide-y">
          <Row
            label="Past requests"
            value={card1.requestCount > 0 ? card1.requestCount.toLocaleString() : null}
          />
          <Row
            label="Quotes received"
            value={card1.quotesReceived > 0 ? card1.quotesReceived.toLocaleString() : null}
          />
          <Row label="Low" value={fmtMoney(card1.low)} />
          <Row label="Avg" value={fmtMoney(card1.avg)} />
          <Row label="High" value={fmtMoney(card1.high)} />
          <Row
            label="Avg spread"
            value={card1.spreadPct != null ? `${card1.spreadPct.toFixed(1)}%` : null}
          />
          <Row
            label="Avg $/mi"
            value={card1.rpmAvg != null ? `$${card1.rpmAvg.toFixed(2)}` : null}
          />
          <Row
            label="Conversion rate"
            value={
              card1.conversionRate != null
                ? `${Math.round(card1.conversionRate * 100)}%`
                : null
            }
          />
          <Row
            label="Last quoted"
            value={card1.lastQuotedOn ? fmtDate(new Date(card1.lastQuotedOn).toISOString()) : null}
          />
        </div>
      </Card>

      {/* Card 2 — broker activity on this lane */}
      <Card className="p-4">
        <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2">
          Broker Activity
        </div>
        {brokerStats.loading ? (
          <div className="h-12 rounded bg-muted animate-pulse" />
        ) : brokerStats.avgResponseMs == null && brokerStats.uniqueBrokers == null ? (
          <div className="text-xs text-muted-foreground py-2">
            No broker responses on this lane yet.
          </div>
        ) : (
          <div className="divide-y">
            <Row
              label="Avg response time"
              value={brokerStats.avgResponseMs != null ? fmtDuration(brokerStats.avgResponseMs) : null}
            />
            <Row
              label="Unique brokers quoting"
              value={brokerStats.uniqueBrokers != null ? String(brokerStats.uniqueBrokers) : null}
            />
          </div>
        )}
      </Card>
    </aside>
  );
}

export default LaneAnalyticsPanel;
